/**
 * Search Filter Section Component
 * Displays the search and filter controls for the VPE Dashboard
 * Extracted from VPEDashboard.tsx for better modularity
 */

import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search } from 'lucide-react';

interface SearchFilterSectionProps {
  searchBy: string;
  searchTerm: string;
  onSearchByChange: (value: string) => void;
  onSearchTermChange: (value: string) => void;
  onSearch: () => void;
  onClearSearch: () => void;
}

const SearchFilterSection: React.FC<SearchFilterSectionProps> = ({
  searchBy,
  searchTerm,
  onSearchByChange,
  onSearchTermChange,
  onSearch,
  onClearSearch,
}) => {
  return (
    <Card className="mb-8 bg-white/90 backdrop-blur-sm shadow-xl border-0 rounded-2xl" data-testid="ea-search-filter-section">
      <CardContent className="p-8" data-testid="ea-search-filter-content">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div data-testid="ea-search-by-container">
            <label className="block text-sm font-bold text-gray-700 mb-4">Search By</label>
            <Select value={searchBy} onValueChange={onSearchByChange} data-testid="ea-search-by-select">
              <SelectTrigger className="w-full h-12 rounded-xl bg-gray-50 border-gray-200 shadow-sm hover:bg-gray-100 transition-colors" data-testid="ea-search-by-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="rounded-xl shadow-lg border-gray-100" data-testid="ea-search-by-content">
                <SelectItem value="group_name" data-testid="ea-search-by-group-name">Group Name</SelectItem>
                <SelectItem value="group_id" data-testid="ea-search-by-group-id">Group ID</SelectItem>
                <SelectItem value="group_type" data-testid="ea-search-by-group-type">Group Type</SelectItem>
                <SelectItem value="clinic_name" data-testid="ea-search-by-clinic-name">Clinic Name</SelectItem>
                <SelectItem value="assigned_vpe" data-testid="ea-search-by-assigned-vpe">Assigned VPE</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div data-testid="ea-search-term-container">
            <label className="block text-sm font-bold text-gray-700 mb-4">Search</label>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <Input 
                placeholder="Enter search term..." 
                value={searchTerm}
                onChange={(e) => onSearchTermChange(e.target.value)}
                className="w-full h-12 pl-12 rounded-xl bg-gray-50 border-gray-200 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 hover:bg-gray-100" 
                data-testid="ea-search-input"
              />
            </div>
          </div>
          <div className="flex items-end space-x-2" data-testid="ea-search-buttons-container">
            <Button
              onClick={onSearch}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-6 py-3 rounded-xl font-semibold shadow-lg hover:shadow-xl transition-all duration-200"
              data-testid="ea-search-button"
            >
              Search
            </Button>
            <Button
              onClick={onClearSearch}
              variant="outline"
              className="px-6 py-3 rounded-xl"
              data-testid="ea-clear-search-button"
            >
              Clear
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default SearchFilterSection;